import React, { useCallback, useEffect, useMemo } from 'react';
import Animated from 'react-native-reanimated';
import { View } from 'react-native';
import { rem, Button, AppView, Indicator, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import BottomPanel from 'design-system/BottomPanel';
import ListEmptyView from 'design-system/ListEmptyView';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useFindMyAddress } from 'hooks';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { OnboardingFlow } from 'utils/types/navigation';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { useNavigateIfAddressInLoginNav } from '../hooks/useNavigateIfAddressInLoginNav';
import { AddressRowItem } from '../personal-details/FindMyAddressScreen';
const ChooseAddressScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    useNavigateIfAddressInLoginNav();
    const flow = route.params?.flow;
    useEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.elements.primary,
            headerRight: () => (flow === OnboardingFlow.RentReporting ||
                flow === OnboardingFlow.Pots) && (<HelpScreenButton articlesKey={flow === OnboardingFlow.RentReporting
                    ? HELP_ARTICLES_KEY.RENT_REPORTING_ONBOARDING
                    : HELP_ARTICLES_KEY.POTS_ONBOARDING}/>),
        });
    }, [colors.elements.primary, flow, route.params.flow]);
    const { sortedAddresses, isFetching } = useFindMyAddress(route.params?.postCode);
    const lastAddressIndex = sortedAddresses.length - 1;
    const handleEnterManually = useCallback(() => {
        if (route.params && 'childDetails' in route.params) {
            navigation.navigateDeprecated('AddressInputScreen', {
                flow: route.params.flow,
                accountId: route.params.accountId,
                childDetails: route.params.childDetails,
            });
            return;
        }
        navigation.navigateDeprecated('AddressInputScreen', {
            flow: route.params.flow,
            isAddingSecondaryAddress: route.params.isAddingSecondaryAddress,
        });
    }, [route.params]);
    const handlePress = useCallback((address) => () => {
        if (route.params && 'childDetails' in route.params) {
            navigation.navigateDeprecated('AddressInputScreen', {
                address,
                flow: route.params.flow,
                accountId: route.params.accountId,
                childDetails: route.params.childDetails,
            });
            return;
        }
        navigation.navigateDeprecated('AddressInputScreen', {
            address,
            flow: route.params.flow,
            isAddingSecondaryAddress: route.params.isAddingSecondaryAddress,
        });
    }, [route.params]);
    const renderItem = useCallback(({ item: address, index }) => (<View style={[
            styles.row,
            index === 0 && styles.firstItem,
            index === lastAddressIndex && styles.lastItem,
        ]}>
          <AddressRowItem withArrow address={address} onPress={handlePress(address)}/>
        </View>), [
        handlePress,
        lastAddressIndex,
        styles.firstItem,
        styles.lastItem,
        styles.row,
    ]);
    const ListEmptyComponent = useMemo(() => isFetching ? (<Indicator dark style={styles.indicator}/>) : (<ListEmptyView onCard subTitle="No address found"/>), [isFetching, styles.indicator]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    return (<AppView withFrame={false}>
        <Animated.FlatList data={sortedAddresses} renderItem={renderItem} onScroll={scrollHandler} scrollEventThrottle={16} showsVerticalScrollIndicator={false} ListHeaderComponent={<ScreenInfoHeader title={flow === OnboardingFlow.JISA
                ? 'Choose your child’s address'
                : 'Choose your address'}/>} ListEmptyComponent={ListEmptyComponent} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={[
            styles.contentContainerStyle,
            paddingHorizontalStyle,
        ]}/>
        <BottomPanel>
          <Button brand title="Enter manually" onPress={handleEnterManually}/>
        </BottomPanel>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export default ChooseAddressScreen;
const styleSet = createStyleSheets((colors) => ({
    indicator: {
        marginTop: '70%',
    },
    row: {
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
    },
    contentContainerStyle: {
        paddingBottom: rem(16),
    },
    firstItem: {
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
    },
    lastItem: {
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
    },
}));
